/**
 * ReelsPage — Page Cinéma PLEIN ÉCRAN style TikTok/Instagram Reels
 * 
 * Design:
 * - Vidéo plein écran edge-to-edge (pas de marge, pas de gap)
 * - Fond noir
 * - Onglets transparents overlaid SUR la vidéo
 * - Actions (like, comment, share) à droite SUR la vidéo
 * - Info auteur en bas SUR la vidéo
 * - Swipe vertical pour changer de vidéo
 * - Header Réseau discret en haut
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Heart, MessageCircle, Share2, Play, Pause, Volume2, VolumeX, 
  Music, Bookmark, MoreHorizontal, ChevronUp, User, X, Video, 
  Upload, MapPin, Hash, FileText, Camera, Check, Loader2,
  AtSign, Package, Briefcase, Megaphone, Search, ShoppingBag
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Onglets - IDENTIQUES au reste du mode Réseau
const TABS = [
  { id: 'tous', label: 'Tous' },
  { id: 'mes_pros', label: 'Mes pros' },
  { id: 'univers', label: 'Univers' },
  { id: 'tendances', label: 'Tendances' },
];

// Sous-filtres UNIVERS (liste complète)
const METIER_FILTERS = [
  { id: 'paysagisme', label: 'Paysagisme', emoji: '🌳' },
  { id: 'jardinage', label: 'Jardinage', emoji: '🌿' },
  { id: 'piscine', label: 'Piscine & Spa', emoji: '🏊' },
  { id: 'electricite', label: 'Électricité', emoji: '⚡' },
  { id: 'plomberie', label: 'Plomberie', emoji: '🔧' },
  { id: 'carrelage', label: 'Carrelage', emoji: '🔲' },
  { id: 'peinture', label: 'Peinture', emoji: '🎨' },
  { id: 'menuiserie', label: 'Menuiserie', emoji: '🪵' },
  { id: 'terrassement', label: 'Terrassement', emoji: '🚜' },
  { id: 'maconnerie', label: 'Maçonnerie', emoji: '🧱' },
  { id: 'renovation', label: 'Rénovation', emoji: '🔨' },
  { id: 'chauffage', label: 'Chauffage', emoji: '🌡️' },
  { id: 'couverture', label: 'Couverture', emoji: '🏠' },
  { id: 'climatisation', label: 'Climatisation', emoji: '❄️' },
  { id: 'facade', label: 'Façade', emoji: '🏗️' },
  { id: 'serrurerie', label: 'Serrurerie', emoji: '🔐' },
  { id: 'vitrerie', label: 'Vitrerie', emoji: '🪟' },
  { id: 'charpente', label: 'Charpente', emoji: '🪓' },
  { id: 'decoration', label: 'Décoration', emoji: '🛋️' },
  { id: 'solaire', label: 'Solaire & Énergie', emoji: '☀️' },
];

// ══════════════════════════════════════════════════════════════════════════════
// DONNÉES DÉMO - COCKPIT INTERCONNECTÉ
// ══════════════════════════════════════════════════════════════════════════════

// Personnes à taguer (abonnés/utilisateurs)
const DEMO_USERS_TO_TAG = [
  { id: 'u1', name: 'Sophie Martin', username: 'sophie_martin', type: 'particulier', avatar: null, color: '#EC4899' },
  { id: 'u2', name: 'Jean Dupont', username: 'jean_dupont', type: 'pro', metier: 'Électricien', avatar: null, color: '#3B82F6' },
  { id: 'u3', name: 'Marie Lambert', username: 'marie_lambert', type: 'particulier', avatar: null, color: '#8B5CF6' },
  { id: 'u4', name: 'Pierre Durand', username: 'pierre_durand', type: 'pro', metier: 'Plombier', avatar: null, color: '#10B981' },
  { id: 'u5', name: 'Lucas Bernard', username: 'lucas_bernard', type: 'pro', metier: 'Paysagiste', avatar: null, color: '#F59E0B' },
  { id: 'u6', name: 'Emma Petit', username: 'emma_petit', type: 'particulier', avatar: null, color: '#EF4444' },
  { id: 'u7', name: 'Thomas Moreau', username: 'thomas_moreau', type: 'pro', metier: 'Carreleur', avatar: null, color: '#06B6D4' },
];

// Produits du catalogue PRO (Cockpit Pro)
const DEMO_PRODUCTS = [
  { id: 'prod_1', code: 'ROB-001', name: 'Robinet mitigeur chromé', price: 89.90, image: 'https://images.unsplash.com/photo-1585704032915-c3400ca199e7?w=100&h=100&fit=crop' },
  { id: 'prod_2', code: 'JNT-010', name: 'Kit joints universels x20', price: 24.50, image: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=100&h=100&fit=crop' },
  { id: 'prod_3', code: 'TUY-PVC', name: 'Tuyau PVC 2m Ø40mm', price: 12.90, image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=100&h=100&fit=crop' },
  { id: 'prod_4', code: 'SIP-200', name: 'Siphon inox premium', price: 45.00, image: 'https://images.unsplash.com/photo-1585771724684-38269d6639fd?w=100&h=100&fit=crop' },
  { id: 'prod_5', code: 'FLX-080', name: 'Flexible inox 80cm', price: 18.90, image: 'https://images.unsplash.com/photo-1562259949-e8e7689d7828?w=100&h=100&fit=crop' },
];

// Prestations de service PRO (Cockpit Pro)
const DEMO_SERVICES = [
  { id: 'serv_1', code: 'PRST-001', name: 'Dépannage plomberie urgence', price: 120.00, duration: '1h', category: 'Plomberie' },
  { id: 'serv_2', code: 'PRST-002', name: 'Installation robinetterie complète', price: 250.00, duration: '2h', category: 'Plomberie' },
  { id: 'serv_3', code: 'PRST-003', name: 'Rénovation salle de bain', price: 3500.00, duration: '5j', category: 'Rénovation' },
  { id: 'serv_4', code: 'PRST-004', name: 'Débouchage canalisation', price: 85.00, duration: '1h', category: 'Plomberie' },
  { id: 'serv_5', code: 'PRST-005', name: 'Installation chauffe-eau', price: 450.00, duration: '3h', category: 'Chauffage' },
];

// Annonces PARTICULIER (Cockpit Particulier)
const DEMO_ANNONCES = [
  { id: 'ann_1', title: 'Recherche carreleur pour SDB', budget: '2000-3000 €', status: 'active', date: 'Il y a 3j', category: 'Carrelage' },
  { id: 'ann_2', title: 'Peinture chambre enfant', budget: '500-800 €', status: 'active', date: 'Il y a 1 sem', category: 'Peinture' },
  { id: 'ann_3', title: 'Réfection terrasse bois', budget: '4000-6000 €', status: 'active', date: 'Il y a 2j', category: 'Menuiserie' },
  { id: 'ann_4', title: 'Installation climatisation', budget: '3000-4000 €', status: 'terminée', date: 'Il y a 2 sem', category: 'Climatisation' },
];

// ══════════════════════════════════════════════════════════════════════════════
// MODAL PUBLIER UN REEL - Style Instagram Camera
// ══════════════════════════════════════════════════════════════════════════════

export function PublishReelModal({ isOpen, onClose, onPublish }) {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [videoClips, setVideoClips] = useState([]); // Tous les clips enregistrés
  const [videoPreview, setVideoPreview] = useState(null);
  const [step, setStep] = useState('camera'); // 'camera', 'details'
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [facingMode, setFacingMode] = useState('environment'); // 'environment' (arrière) ou 'user' (selfie)
  const [captureMode, setCaptureMode] = useState('3min'); // 'story' (90s), '3min', 'photo'
  const [photos, setPhotos] = useState([]);
  
  // Formulaire
  const [description, setDescription] = useState('');
  const [hashtags, setHashtags] = useState('');
  const [location, setLocation] = useState('');
  const [selectedMetier, setSelectedMetier] = useState(''); // OBLIGATOIRE
  
  // Nouveaux champs - Interconnexion Cockpit
  const [taggedUsers, setTaggedUsers] = useState([]);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [showUserSearch, setShowUserSearch] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedAnnonce, setSelectedAnnonce] = useState(null);
  const [productSearchQuery, setProductSearchQuery] = useState('');
  const [serviceSearchQuery, setServiceSearchQuery] = useState('');
  const [annonceSearchQuery, setAnnonceSearchQuery] = useState('');
  
  // Simuler le type d'utilisateur (Pro ou Particulier) - En prod, viendrait du contexte auth
  const [userType, setUserType] = useState('pro'); // 'pro' ou 'particulier'
  
  const videoInputRef = useRef(null);
  const photoInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const videoChunksRef = useRef([]);
  const streamRef = useRef(null);
  const previewVideoRef = useRef(null);
  const recordingTimerRef = useRef(null);
  const canvasRef = useRef(null);
  
  const CAPTURE_MODES = {
    story: { label: 'Story', maxDuration: 90, expiresIn: '7j' },
    '3min': { label: '3\u00A0min', maxDuration: 180, expiresIn: null }, // \u00A0 = non-breaking space
    photo: { label: 'Photo(s)', maxPhotos: 10, aspectRatio: '4/5' },
  };
  const MAX_DURATION = captureMode === 'story' ? 90 : 180;
  const MAX_PHOTOS = 10;
  const MAX_DESCRIPTION = 2200;
  
  // Ouvrir caméra automatiquement à l'ouverture
  useEffect(() => {
    if (isOpen && step === 'camera') {
      initCamera('environment'); // Caméra arrière par défaut
    }
    return () => {
      stopCamera();
    };
  }, [isOpen]);
  
  // Reset au close
  useEffect(() => {
    if (!isOpen) {
      setStep('camera');
      setVideoClips([]);
      setVideoPreview(null);
      setDescription('');
      setHashtags('');
      setLocation('');
      setSelectedMetier('');
      setIsRecording(false);
      setRecordingTime(0);
      setCameraReady(false);
      setCameraError(null);
      setFacingMode('environment');
      setCaptureMode('3min');
      setPhotos([]);
      stopCamera();
    }
  }, [isOpen]);
  
  // Stop caméra
  const stopCamera = () => {
    if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      try { mediaRecorderRef.current.stop(); } catch(e) {}
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => { t.stop(); t.enabled = false; });
      streamRef.current = null;
    }
    if (previewVideoRef.current) {
      previewVideoRef.current.srcObject = null;
    }
    setIsRecording(false);
  };
  
  // Init caméra
  const initCamera = async (facing) => {
    setCameraReady(false);
    setCameraError(null);
    
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: facing }, 
        audio: true 
      });
      streamRef.current = stream;
      if (previewVideoRef.current) {
        previewVideoRef.current.srcObject = stream;
      }
      setFacingMode(facing);
      setCameraReady(true);
    } catch (err) {
      console.error('Erreur 1:', err.name);
      // FALLBACK: essayer avec deviceId
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const cameras = devices.filter(d => d.kind === 'videoinput');
        console.log('Cameras trouvées:', cameras.length);
        if (cameras.length > 1) {
          const idx = facing === 'user' ? 1 : 0;
          const stream = await navigator.mediaDevices.getUserMedia({ 
            video: { deviceId: cameras[idx].deviceId }, 
            audio: true 
          });
          streamRef.current = stream;
          if (previewVideoRef.current) {
            previewVideoRef.current.srcObject = stream;
          }
          setFacingMode(facing);
          setCameraReady(true);
        } else {
          setCameraError('1 caméra: ' + err.name);
        }
      } catch (e2) {
        setCameraError(err.name + ' / ' + e2.name);
      }
    }
  };
  
  // Flip caméra - LAISSER LE NAVIGATEUR GÉRER
  const flipCamera = async () => {
    setCameraReady(false);
    setCameraError(null);
    
    const newFacing = facingMode === 'environment' ? 'user' : 'environment';
    
    try {
      // Obtenir la nouvelle caméra DIRECTEMENT (le navigateur libère l'ancienne)
      const newStream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: newFacing }, 
        audio: true 
      });
      
      // Succès - maintenant nettoyer l'ancien
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
      }
      
      streamRef.current = newStream;
      if (previewVideoRef.current) {
        previewVideoRef.current.srcObject = newStream;
      }
      setFacingMode(newFacing);
      setCameraReady(true);
    } catch (err) {
      setCameraError(err.name);
    }
  };
  
  // Démarrer/Arrêter enregistrement (pause/reprise pour multi-clips)
  const toggleRecording = () => {
    if (isRecording) {
      // PAUSE - Arrêter le clip actuel et le sauvegarder
      if (mediaRecorderRef.current) {
        mediaRecorderRef.current.stop();
      }
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
      setIsRecording(false);
    } else {
      // REPRENDRE - Démarrer un nouveau clip
      if (!streamRef.current) return;
      if (recordingTime >= MAX_DURATION) return; // Max atteint
      
      const mediaRecorder = new MediaRecorder(streamRef.current);
      mediaRecorderRef.current = mediaRecorder;
      videoChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          videoChunksRef.current.push(e.data);
        }
      };
      
      mediaRecorder.onstop = () => {
        // Sauvegarder ce clip
        const blob = new Blob(videoChunksRef.current, { type: 'video/webm' });
        setVideoClips(prev => [...prev, blob]);
      };
      
      mediaRecorder.start();
      setIsRecording(true);
      
      // Timer (continue depuis le temps actuel)
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime(prev => {
          if (prev >= MAX_DURATION) {
            toggleRecording(); // Auto-stop à 3 min
            return prev;
          }
          return prev + 1;
        });
      }, 1000);
    }
  };
  
  // Aller à la page de publication (fusionner les clips)
  const goToPublish = async () => {
    if (videoClips.length === 0) return;
    
    // Fusionner tous les clips en un seul blob
    const mergedBlob = new Blob(videoClips, { type: 'video/webm' });
    setVideoPreview(URL.createObjectURL(mergedBlob));
    stopCamera();
    setStep('details');
  };
  
  // Gestion import galerie
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.onloadedmetadata = () => {
        if (video.duration > MAX_DURATION) {
          alert(`La vidéo ne doit pas dépasser ${MAX_DURATION / 60} minutes`);
          return;
        }
        setVideoClips([file]);
        setVideoPreview(URL.createObjectURL(file));
        stopCamera();
        setStep('details');
      };
      video.src = URL.createObjectURL(file);
    }
  };
  
  // Publier
  const handlePublish = async () => {
    // Vérifier qu'on a du contenu selon le mode
    if (captureMode === 'photo') {
      if (photos.length === 0) return;
    } else {
      if (videoClips.length === 0) return;
    }
    
    // Vérifier que le métier est sélectionné (OBLIGATOIRE)
    if (!selectedMetier) return;
    
    setIsUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('description', description);
      formData.append('hashtags', hashtags);
      formData.append('location', location);
      formData.append('metier', selectedMetier); // OBLIGATOIRE
      formData.append('type', captureMode); // 'story', '3min', 'photo'
      
      if (captureMode === 'photo') {
        // Envoyer les photos
        photos.forEach((photo, idx) => {
          formData.append(`photo_${idx}`, photo.blob, `photo_${idx}.jpg`);
        });
        formData.append('photo_count', photos.length.toString());
      } else {
        // Fusionner tous les clips vidéo
        const mergedBlob = new Blob(videoClips, { type: 'video/webm' });
        formData.append('video', mergedBlob, 'reel.webm');
        if (captureMode === 'story') {
          formData.append('expires_in', '7d');
        }
      }
      
      const token = localStorage.getItem('ecosystem_token');
      const endpoint = captureMode === 'photo' 
        ? `${API_URL}/api/reseau/posts/publish`
        : (captureMode === 'story' ? `${API_URL}/api/reseau/stories/publish` : `${API_URL}/api/reseau/reels/publish`);
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        body: formData,
      });
      
      if (response.ok) {
        onPublish?.();
        onClose();
      } else {
        // Mode démo
        setTimeout(() => { onPublish?.(); onClose(); }, 800);
      }
    } catch (error) {
      setTimeout(() => { onPublish?.(); onClose(); }, 800);
    } finally {
      setIsUploading(false);
    }
  };
  
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  // Prendre une photo (mode Photo)
  const takePhoto = () => {
    if (!streamRef.current || !previewVideoRef.current) return;
    if (photos.length >= MAX_PHOTOS) return;
    
    const video = previewVideoRef.current;
    const canvas = document.createElement('canvas');
    
    // Format 4:5 obligatoire
    const targetRatio = 4 / 5;
    const videoWidth = video.videoWidth;
    const videoHeight = video.videoHeight;
    
    let cropWidth, cropHeight, cropX, cropY;
    
    if (videoWidth / videoHeight > targetRatio) {
      // Vidéo plus large - crop horizontal
      cropHeight = videoHeight;
      cropWidth = videoHeight * targetRatio;
      cropX = (videoWidth - cropWidth) / 2;
      cropY = 0;
    } else {
      // Vidéo plus haute - crop vertical
      cropWidth = videoWidth;
      cropHeight = videoWidth / targetRatio;
      cropX = 0;
      cropY = (videoHeight - cropHeight) / 2;
    }
    
    canvas.width = cropWidth;
    canvas.height = cropHeight;
    
    const ctx = canvas.getContext('2d');
    
    // Si selfie, mirror l'image
    if (facingMode === 'user') {
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
    }
    
    ctx.drawImage(video, cropX, cropY, cropWidth, cropHeight, 0, 0, cropWidth, cropHeight);
    
    canvas.toBlob((blob) => {
      if (blob) {
        const photoUrl = URL.createObjectURL(blob);
        setPhotos(prev => [...prev, { blob, url: photoUrl }]);
      }
    }, 'image/jpeg', 0.9);
  };
  
  // Supprimer une photo
  const removePhoto = (index) => {
    setPhotos(prev => {
      const newPhotos = [...prev];
      URL.revokeObjectURL(newPhotos[index].url);
      newPhotos.splice(index, 1);
      return newPhotos;
    });
  };
  
  // Aller à la publication (photos)
  const goToPublishPhotos = () => {
    if (photos.length === 0) return;
    stopCamera();
    setStep('details');
  };
  
  // Gestion import galerie photos
  const handlePhotoFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    const remaining = MAX_PHOTOS - photos.length;
    const toAdd = files.slice(0, remaining);
    
    toAdd.forEach(file => {
      const reader = new FileReader();
      reader.onload = () => {
        const img = new Image();
        img.onload = () => {
          // Créer canvas 4:5
          const canvas = document.createElement('canvas');
          const targetRatio = 4 / 5;
          
          let cropWidth, cropHeight, cropX, cropY;
          if (img.width / img.height > targetRatio) {
            cropHeight = img.height;
            cropWidth = img.height * targetRatio;
            cropX = (img.width - cropWidth) / 2;
            cropY = 0;
          } else {
            cropWidth = img.width;
            cropHeight = img.width / targetRatio;
            cropX = 0;
            cropY = (img.height - cropHeight) / 2;
          }
          
          canvas.width = cropWidth;
          canvas.height = cropHeight;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, cropX, cropY, cropWidth, cropHeight, 0, 0, cropWidth, cropHeight);
          
          canvas.toBlob((blob) => {
            if (blob) {
              const photoUrl = URL.createObjectURL(blob);
              setPhotos(prev => {
                if (prev.length < MAX_PHOTOS) {
                  return [...prev, { blob, url: photoUrl }];
                }
                return prev;
              });
            }
          }, 'image/jpeg', 0.9);
        };
        img.src = reader.result;
      };
      reader.readAsDataURL(file);
    });
    
    e.target.value = '';
  };
  
  if (!isOpen) return null;
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: '100dvh',
        minHeight: '-webkit-fill-available',
        background: step === 'details' ? '#1E3A5F' : '#000',
        zIndex: 10000,
        display: 'flex',
        flexDirection: 'column',
        touchAction: step === 'camera' ? 'none' : 'auto',
        userSelect: 'none',
        overflow: 'hidden',
        WebkitOverflowScrolling: 'touch',
      }}
    >
      {/* ═══ ÉCRAN CAMÉRA ═══ */}
      {step === 'camera' && (
        <>
          {/* Header */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px',
          }}>
            <button
              onClick={onClose}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 8 }}
            >
              <X size={28} color="#fff" />
            </button>
            
            {/* Timer si enregistrement ou clips (vidéo seulement, pas photo) */}
            {captureMode !== 'photo' && (isRecording || recordingTime > 0) && (
              <div style={{
                background: isRecording ? 'rgba(255,59,48,0.9)' : 'rgba(0,0,0,0.6)',
                padding: '8px 20px',
                borderRadius: 24,
                display: 'flex',
                alignItems: 'center',
                gap: 10,
              }}>
                {isRecording && (
                  <div style={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    background: '#fff',
                    animation: 'pulse 1s infinite',
                  }} />
                )}
                <span style={{ color: '#fff', fontSize: 16, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                  {formatTime(recordingTime)} / {formatTime(MAX_DURATION)}
                </span>
              </div>
            )}
            
            {/* Compteur photos en mode photo */}
            {captureMode === 'photo' && photos.length > 0 && (
              <div style={{
                background: 'rgba(0,0,0,0.6)',
                padding: '8px 20px',
                borderRadius: 24,
              }}>
                <span style={{ color: '#fff', fontSize: 16, fontWeight: 700 }}>
                  {photos.length}/{MAX_PHOTOS} photos
                </span>
              </div>
            )}
            
            {/* Bouton Flip Camera en haut à droite */}
            <button
              onClick={flipCamera}
              disabled={!cameraReady}
              style={{
                width: 44,
                height: 44,
                borderRadius: '50%',
                background: 'rgba(0,0,0,0.4)',
                backdropFilter: 'blur(8px)',
                border: 'none',
                cursor: cameraReady ? 'pointer' : 'not-allowed',
                opacity: cameraReady ? 1 : 0.4,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M11 19H4a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h5"/>
                <path d="M13 5h7a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2h-5"/>
                <circle cx="12" cy="12" r="3"/>
                <path d="m18 22-3-3 3-3"/>
                <path d="m6 2 3 3-3 3"/>
              </svg>
            </button>
          </div>
          
          {/* Preview Caméra - Plein écran */}
          <div style={{ flex: 1, position: 'relative' }}>
            <video
              ref={previewVideoRef}
              autoPlay
              muted
              playsInline
              webkit-playsinline="true"
              style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                transform: facingMode === 'user' ? 'scaleX(-1)' : 'none',
                background: '#000',
              }}
            />
            
            {/* ═══ MASQUE 4:5 EN MODE PHOTO ═══ */}
            {captureMode === 'photo' && cameraReady && (
              <>
                {/* Bande noire en haut */}
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 80,
                  background: 'rgba(0,0,0,0.7)',
                  pointerEvents: 'none',
                  zIndex: 5,
                }} />
                {/* Bande noire en bas - plus grande pour laisser place aux contrôles */}
                <div style={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: 200,
                  background: 'rgba(0,0,0,0.7)',
                  pointerEvents: 'none',
                  zIndex: 5,
                }} />
                {/* Cadre 4:5 - positionné plus haut */}
                <div style={{
                  position: 'absolute',
                  top: 80,
                  left: 0,
                  right: 0,
                  bottom: 200,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  pointerEvents: 'none',
                  zIndex: 6,
                }}>
                  <div style={{
                    width: '100%',
                    maxWidth: 'calc((100vh - 280px) * 0.8)',
                    aspectRatio: '4/5',
                    border: '2px solid rgba(255,255,255,0.4)',
                    borderRadius: 4,
                  }} />
                </div>
              </>
            )}
            
            {/* Placeholder si pas de caméra */}
            {!cameraReady && (
              <div style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#111',
                padding: 20,
              }}>
                <Camera size={64} color="rgba(255,255,255,0.3)" />
                <p style={{ color: '#fff', marginTop: 16, fontSize: 15, fontWeight: 500, textAlign: 'center' }}>
                  {cameraError || 'Chargement de la caméra...'}
                </p>
                {cameraError && (
                  <>
                    <button
                      onClick={() => initCamera(facingMode)}
                      style={{
                        marginTop: 16,
                        padding: '12px 24px',
                        background: '#F97316',
                        border: 'none',
                        borderRadius: 8,
                        color: '#fff',
                        fontSize: 14,
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                    >
                      Réessayer
                    </button>
                    <p style={{ color: 'rgba(255,255,255,0.5)', marginTop: 16, fontSize: 13, textAlign: 'center' }}>
                      ou importe une vidéo depuis ta galerie
                    </p>
                  </>
                )}
              </div>
            )}
          </div>
          
          {/* ═══ ZONE CONTRÔLES BAS - Centrée et fixe ═══ */}
          <div style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            paddingBottom: 'max(28px, env(safe-area-inset-bottom))',
            paddingTop: 30,
            zIndex: 200,
            touchAction: 'none',
            WebkitUserSelect: 'none',
            userSelect: 'none',
          }}>
            {/* Sélecteur de mode - 3 colonnes égales */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr',
              width: 280,
              marginBottom: 20,
            }}>
              {Object.entries(CAPTURE_MODES).map(([key, mode]) => (
                <button
                  key={key}
                  onClick={() => {
                    setCaptureMode(key);
                    if (key === 'photo') {
                      setPhotos([]);
                    } else {
                      setVideoClips([]);
                      setRecordingTime(0);
                    }
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '8px 0',
                    position: 'relative',
                    touchAction: 'manipulation',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                  }}
                >
                  <span style={{
                    color: '#fff',
                    fontSize: 16,
                    fontWeight: captureMode === key ? 700 : 500,
                    textShadow: '0 2px 8px rgba(0,0,0,0.8)',
                    opacity: captureMode === key ? 1 : 0.55,
                    whiteSpace: 'nowrap',
                  }}>
                    {mode.label}
                  </span>
                  {captureMode === key && (
                    <div style={{
                      marginTop: 6,
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      background: '#F97316',
                    }} />
                  )}
                </button>
              ))}
            </div>

            {/* Bouton principal */}
            <button
              onClick={captureMode === 'photo' ? takePhoto : toggleRecording}
              disabled={captureMode === 'photo' ? (!cameraReady || photos.length >= MAX_PHOTOS) : (!cameraReady || recordingTime >= MAX_DURATION)}
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                margin: 0,
                cursor: captureMode === 'photo' 
                  ? ((cameraReady && photos.length < MAX_PHOTOS) ? 'pointer' : 'not-allowed')
                  : ((cameraReady && recordingTime < MAX_DURATION) ? 'pointer' : 'not-allowed'),
                opacity: captureMode === 'photo' 
                  ? ((cameraReady && photos.length < MAX_PHOTOS) ? 1 : 0.5)
                  : ((cameraReady && recordingTime < MAX_DURATION) ? 1 : 0.5),
                touchAction: 'manipulation',
                WebkitTapHighlightColor: 'transparent',
              }}
            >
              <div style={{
                width: 76,
                height: 76,
                borderRadius: '50%',
                background: isRecording ? '#ff3b30' : '#F97316',
                border: '5px solid #fff',
                boxShadow: '0 4px 24px rgba(0,0,0,0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                {captureMode === 'photo' ? (
                  <div style={{ width: 60, height: 60, borderRadius: '50%', background: '#fff' }} />
                ) : isRecording ? (
                  <div style={{ width: 28, height: 28, borderRadius: 6, background: '#fff' }} />
                ) : (
                  <div style={{ width: 60, height: 60, borderRadius: '50%', background: '#F97316' }} />
                )}
              </div>
            </button>
          </div>

          {/* Bouton Galerie flottant en bas à gauche */}
          <button
            onClick={() => captureMode === 'photo' ? photoInputRef.current?.click() : videoInputRef.current?.click()}
            style={{
              position: 'absolute',
              left: 20,
              bottom: 32,
              width: 48,
              height: 48,
              borderRadius: 12,
              background: 'rgba(0,0,0,0.4)',
              backdropFilter: 'blur(8px)',
              border: '1px solid rgba(255,255,255,0.2)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 201,
              touchAction: 'none',
            }}
          >
            <Upload size={22} color="#fff" />
          </button>

          {/* Bouton OK flottant en bas à droite (visible si clips vidéo OU photos) */}
          {((captureMode !== 'photo' && videoClips.length > 0 && !isRecording) || (captureMode === 'photo' && photos.length > 0)) && (
            <button
              onClick={captureMode === 'photo' ? goToPublishPhotos : goToPublish}
              style={{
                position: 'absolute',
                right: 20,
                bottom: 40,
                height: 48,
                paddingLeft: 20,
                paddingRight: 20,
                borderRadius: 24,
                background: '#F97316',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                zIndex: 200,
                boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
              }}
            >
              <Check size={20} color="#fff" />
              <span style={{ color: '#fff', fontSize: 14, fontWeight: 600 }}>OK</span>
            </button>
          )}
          
          {/* ═══ PREVIEW DES PHOTOS PRISES (mode photo) ═══ */}
          {captureMode === 'photo' && photos.length > 0 && (
            <div style={{
              position: 'absolute',
              bottom: 100,
              left: 16,
              right: 80,
              display: 'flex',
              gap: 8,
              overflowX: 'auto',
              zIndex: 200,
              paddingBottom: 4,
            }}>
              {photos.map((photo, idx) => (
                <div key={idx} style={{ position: 'relative', flexShrink: 0 }}>
                  <img 
                    src={photo.url} 
                    alt={`Photo ${idx + 1}`}
                    style={{
                      width: 56,
                      height: 70, // Ratio 4:5
                      objectFit: 'cover',
                      borderRadius: 8,
                      border: '2px solid rgba(255,255,255,0.4)',
                    }}
                  />
                  <button
                    onClick={() => removePhoto(idx)}
                    style={{
                      position: 'absolute',
                      top: -6,
                      right: -6,
                      width: 20,
                      height: 20,
                      borderRadius: '50%',
                      background: '#ff3b30',
                      border: 'none',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <X size={12} color="#fff" />
                  </button>
                </div>
              ))}
            </div>
          )}
          
          {/* Indicateur de clips enregistrés (mode vidéo seulement) */}
          {captureMode !== 'photo' && videoClips.length > 0 && (
            <div style={{
              position: 'absolute',
              bottom: 100,
              left: 16,
              background: 'rgba(0,0,0,0.6)',
              backdropFilter: 'blur(8px)',
              padding: '6px 12px',
              borderRadius: 20,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              zIndex: 200,
            }}>
              <div style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: '#F97316',
              }} />
              <span style={{ color: '#fff', fontSize: 12, fontWeight: 500 }}>
                {videoClips.length} clip{videoClips.length > 1 ? 's' : ''}
              </span>
            </div>
          )}
          
          <input
            ref={videoInputRef}
            type="file"
            accept="video/*"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
          
          <input
            ref={photoInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handlePhotoFileSelect}
            style={{ display: 'none' }}
          />
        </>
      )}
      
      {/* ═══ ÉCRAN DÉTAILS ═══ */}
      {step === 'details' && (
        <div style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          background: '#1E3A5F',
          overflow: 'hidden',
        }}>
          {/* Header */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px',
            borderBottom: '1px solid rgba(255,255,255,0.15)',
            flexShrink: 0,
          }}>
            <button
              onClick={() => { 
                setStep('camera'); 
                setVideoClips([]); 
                setVideoPreview(null); 
                setRecordingTime(0); 
                setPhotos([]);
                initCamera('environment'); 
              }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 8 }}
            >
              <X size={24} color="#fff" />
            </button>
            <span style={{ color: '#fff', fontSize: 16, fontWeight: 600 }}>
              {captureMode === 'photo' ? 'Nouvelle Publication' : (captureMode === 'story' ? 'Nouvelle Story' : 'Nouveau Reel')}
            </span>
            <div style={{ width: 40 }} />
          </div>
          
          {/* Content - SCROLLABLE */}
          <div style={{ 
            flex: 1, 
            overflowY: 'auto', 
            overflowX: 'hidden',
            padding: 16,
            paddingBottom: 40,
            WebkitOverflowScrolling: 'touch',
            touchAction: 'pan-y',
          }}>
            <div style={{ maxWidth: 400, margin: '0 auto' }}>
              {/* Preview vidéo */}
              {videoPreview && captureMode !== 'photo' && (
                <div style={{
                  width: '100%',
                  aspectRatio: '9/16',
                  maxHeight: 280,
                  background: '#000',
                  borderRadius: 12,
                  overflow: 'hidden',
                  marginBottom: 20,
                }}>
                  <video
                    src={videoPreview}
                    controls
                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                  />
                </div>
              )}
              
              {/* Preview photos (mode photo) */}
              {captureMode === 'photo' && photos.length > 0 && (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: photos.length === 1 ? '1fr' : 'repeat(2, 1fr)',
                  gap: 8,
                  marginBottom: 20,
                }}>
                  {photos.map((photo, idx) => (
                    <div 
                      key={idx} 
                      style={{ 
                        aspectRatio: '4/5',
                        borderRadius: 12,
                        overflow: 'hidden',
                        position: 'relative',
                      }}
                    >
                      <img 
                        src={photo.url} 
                        alt={`Photo ${idx + 1}`}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                      />
                      <span style={{
                        position: 'absolute',
                        bottom: 8,
                        right: 8,
                        background: 'rgba(0,0,0,0.6)',
                        color: '#fff',
                        fontSize: 12,
                        fontWeight: 600,
                        padding: '2px 8px',
                        borderRadius: 12,
                      }}>
                        {idx + 1}/{photos.length}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Info Story (7 jours) */}
              {captureMode === 'story' && (
                <div style={{
                  background: 'rgba(249,115,22,0.15)',
                  border: '1px solid rgba(249,115,22,0.3)',
                  borderRadius: 12,
                  padding: 12,
                  marginBottom: 16,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                }}>
                  <span style={{ fontSize: 20 }}>⏱️</span>
                  <span style={{ color: '#F97316', fontSize: 13, fontWeight: 500 }}>
                    Ta story sera visible pendant 7 jours
                  </span>
                </div>
              )}
              
              {/* DÉMO: Toggle Pro/Particulier */}
              <div style={{
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 12,
                padding: 12,
                marginBottom: 16,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ 
                    fontSize: 10, 
                    fontWeight: 700, 
                    color: '#F97316', 
                    background: 'rgba(249,115,22,0.2)', 
                    padding: '2px 6px', 
                    borderRadius: 4 
                  }}>
                    DÉMO
                  </span>
                  <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: 13 }}>
                    Tu publies en tant que :
                  </span>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    onClick={() => {
                      setUserType('pro');
                      setSelectedAnnonce(null);
                    }}
                    style={{
                      padding: '6px 12px',
                      borderRadius: 8,
                      border: 'none',
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: 'pointer',
                      background: userType === 'pro' ? '#3B82F6' : 'rgba(255,255,255,0.1)',
                      color: '#fff',
                    }}
                  >
                    Pro
                  </button>
                  <button
                    onClick={() => {
                      setUserType('particulier');
                      setSelectedProduct(null);
                      setSelectedService(null);
                    }}
                    style={{
                      padding: '6px 12px',
                      borderRadius: 8,
                      border: 'none',
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: 'pointer',
                      background: userType === 'particulier' ? '#EC4899' : 'rgba(255,255,255,0.1)',
                      color: '#fff',
                    }}
                  >
                    Particulier
                  </button>
                </div>
              </div>
              
              {/* Description */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 8, 
                  marginBottom: 8,
                }}>
                  <FileText size={16} color="#F97316" />
                  <span style={{ color: '#fff', fontSize: 14, fontWeight: 500 }}>Description</span>
                  <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: 12, marginLeft: 'auto' }}>
                    {description.length}/{MAX_DESCRIPTION}
                  </span>
                </div>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value.slice(0, MAX_DESCRIPTION))}
                  placeholder="Décris ta réalisation..."
                  style={{
                    width: '100%',
                    minHeight: 80,
                    padding: 12,
                    background: 'rgba(255,255,255,0.1)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    borderRadius: 12,
                    color: '#fff',
                    fontSize: 14,
                    resize: 'vertical',
                    outline: 'none',
                  }}
                />
              </div>
              
              {/* UNIVERS - OBLIGATOIRE */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 8, 
                  marginBottom: 8,
                }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#F97316" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
                  </svg>
                  <span style={{ color: '#fff', fontSize: 14, fontWeight: 500 }}>Univers</span>
                  <span style={{ color: '#F97316', fontSize: 12, fontWeight: 600 }}>*obligatoire</span>
                </div>
                <select
                  value={selectedMetier}
                  onChange={(e) => setSelectedMetier(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '14px 16px',
                    borderRadius: 12,
                    border: selectedMetier ? '2px solid #F97316' : '1px solid rgba(255,255,255,0.3)',
                    background: selectedMetier ? 'rgba(249,115,22,0.15)' : 'rgba(255,255,255,0.1)',
                    color: '#fff',
                    fontSize: 15,
                    fontWeight: 500,
                    cursor: 'pointer',
                    outline: 'none',
                    appearance: 'none',
                    WebkitAppearance: 'none',
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 12px center',
                    backgroundSize: '20px',
                    paddingRight: 44,
                  }}
                >
                  <option value="" style={{ background: '#1E3A5F', color: '#fff' }}>Sélectionne ton univers...</option>
                  {METIER_FILTERS.map((metier) => (
                    <option 
                      key={metier.id} 
                      value={metier.id}
                      style={{ background: '#1E3A5F', color: '#fff' }}
                    >
                      {metier.emoji} {metier.label}
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Hashtags */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 8, 
                  marginBottom: 8,
                }}>
                  <Hash size={16} color="#F97316" />
                  <span style={{ color: '#fff', fontSize: 14, fontWeight: 500 }}>Hashtags</span>
                </div>
                <input
                  value={hashtags}
                  onChange={(e) => setHashtags(e.target.value)}
                  placeholder="#renovation #artisan #plomberie"
                  style={{
                    width: '100%',
                    padding: 12,
                    background: 'rgba(255,255,255,0.1)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    borderRadius: 12,
                    color: '#fff',
                    fontSize: 14,
                    outline: 'none',
                  }}
                />
              </div>
              
              {/* Localisation */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 8, 
                  marginBottom: 8,
                }}>
                  <MapPin size={16} color="#F97316" />
                  <span style={{ color: '#fff', fontSize: 14, fontWeight: 500 }}>Localisation</span>
                </div>
                <input
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder="Marseille, France"
                  style={{
                    width: '100%',
                    padding: 12,
                    background: 'rgba(255,255,255,0.1)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    borderRadius: 12,
                    color: '#fff',
                    fontSize: 14,
                    outline: 'none',
                  }}
                />
              </div>
              
              {/* ══════════════════════════════════════════════════════════════
                  IDENTIFIER DES PERSONNES (Tags @)
                  ══════════════════════════════════════════════════════════════ */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 8, 
                  marginBottom: 8,
                }}>
                  <AtSign size={16} color="#F97316" />
                  <span style={{ color: '#fff', fontSize: 14, fontWeight: 500 }}>Identifier des personnes</span>
                </div>
                
                {/* Users tagués */}
                {taggedUsers.length > 0 && (
                  <div style={{ 
                    display: 'flex', 
                    flexWrap: 'wrap', 
                    gap: 8, 
                    marginBottom: 10,
                  }}>
                    {taggedUsers.map((user) => (
                      <div 
                        key={user.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                          background: 'rgba(249,115,22,0.2)',
                          border: '1px solid rgba(249,115,22,0.4)',
                          borderRadius: 20,
                          padding: '6px 10px',
                        }}
                      >
                        <div style={{
                          width: 20,
                          height: 20,
                          borderRadius: '50%',
                          background: user.color,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 10,
                          fontWeight: 600,
                          color: '#fff',
                        }}>
                          {user.name[0]}
                        </div>
                        <span style={{ color: '#fff', fontSize: 13 }}>@{user.username}</span>
                        <button
                          onClick={() => setTaggedUsers(taggedUsers.filter(u => u.id !== user.id))}
                          style={{ 
                            background: 'none', 
                            border: 'none', 
                            cursor: 'pointer',
                            padding: 0,
                            display: 'flex',
                          }}
                        >
                          <X size={14} color="#fff" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Recherche utilisateurs */}
                <div style={{ position: 'relative' }}>
                  <div style={{ position: 'relative' }}>
                    <Search size={16} color="rgba(255,255,255,0.5)" style={{ 
                      position: 'absolute', 
                      left: 12, 
                      top: '50%', 
                      transform: 'translateY(-50%)' 
                    }} />
                    <input
                      value={userSearchQuery}
                      onChange={(e) => {
                        setUserSearchQuery(e.target.value);
                        setShowUserSearch(true);
                      }}
                      onFocus={() => setShowUserSearch(true)}
                      placeholder="Rechercher @nom..."
                      style={{
                        width: '100%',
                        padding: '12px 12px 12px 40px',
                        background: 'rgba(255,255,255,0.1)',
                        border: '1px solid rgba(255,255,255,0.2)',
                        borderRadius: 12,
                        color: '#fff',
                        fontSize: 14,
                        outline: 'none',
                      }}
                    />
                  </div>
                  
                  {/* Dropdown résultats */}
                  {showUserSearch && userSearchQuery.length > 0 && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      background: '#1a3352',
                      border: '1px solid rgba(255,255,255,0.2)',
                      borderRadius: 12,
                      marginTop: 4,
                      maxHeight: 200,
                      overflowY: 'auto',
                      zIndex: 100,
                    }}>
                      {DEMO_USERS_TO_TAG
                        .filter(u => 
                          !taggedUsers.find(t => t.id === u.id) &&
                          (u.name.toLowerCase().includes(userSearchQuery.toLowerCase()) ||
                           u.username.toLowerCase().includes(userSearchQuery.toLowerCase()))
                        )
                        .slice(0, 5)
                        .map((user) => (
                          <button
                            key={user.id}
                            onClick={() => {
                              setTaggedUsers([...taggedUsers, user]);
                              setUserSearchQuery('');
                              setShowUserSearch(false);
                            }}
                            style={{
                              width: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 10,
                              padding: '10px 12px',
                              background: 'none',
                              border: 'none',
                              borderBottom: '1px solid rgba(255,255,255,0.1)',
                              cursor: 'pointer',
                              textAlign: 'left',
                            }}
                          >
                            <div style={{
                              width: 32,
                              height: 32,
                              borderRadius: '50%',
                              background: user.color,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: 14,
                              fontWeight: 600,
                              color: '#fff',
                            }}>
                              {user.name[0]}
                            </div>
                            <div>
                              <div style={{ color: '#fff', fontSize: 14, fontWeight: 500 }}>{user.name}</div>
                              <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12 }}>
                                @{user.username} • {user.type === 'pro' ? user.metier : 'Particulier'}
                              </div>
                            </div>
                          </button>
                        ))}
                      {DEMO_USERS_TO_TAG.filter(u => 
                        !taggedUsers.find(t => t.id === u.id) &&
                        (u.name.toLowerCase().includes(userSearchQuery.toLowerCase()) ||
                         u.username.toLowerCase().includes(userSearchQuery.toLowerCase()))
                      ).length === 0 && (
                        <div style={{ padding: 16, color: 'rgba(255,255,255,0.5)', textAlign: 'center', fontSize: 13 }}>
                          Aucun utilisateur trouvé
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
              
              {/* ══════════════════════════════════════════════════════════════
                  LIAISON COCKPIT - PRO : Produit
                  ══════════════════════════════════════════════════════════════ */}
              {userType === 'pro' && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 8, 
                    marginBottom: 8,
                  }}>
                    <Package size={16} color="#F97316" />
                    <span style={{ color: '#fff', fontSize: 14, fontWeight: 500 }}>Lier un Produit</span>
                    <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, marginLeft: 'auto' }}>du catalogue</span>
                  </div>
                  
                  {/* Produit sélectionné */}
                  {selectedProduct && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      background: 'rgba(16,185,129,0.15)',
                      border: '1px solid rgba(16,185,129,0.4)',
                      borderRadius: 12,
                      padding: 12,
                      marginBottom: 10,
                    }}>
                      <img 
                        src={selectedProduct.image} 
                        alt=""
                        style={{ width: 48, height: 48, borderRadius: 8, objectFit: 'cover' }}
                      />
                      <div style={{ flex: 1 }}>
                        <div style={{ color: '#fff', fontSize: 14, fontWeight: 500 }}>{selectedProduct.name}</div>
                        <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12 }}>
                          {selectedProduct.code} • {selectedProduct.price.toFixed(2)} €
                        </div>
                      </div>
                      <button
                        onClick={() => setSelectedProduct(null)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
                      >
                        <X size={18} color="#fff" />
                      </button>
                    </div>
                  )}
                  
                  {/* Recherche produit */}
                  {!selectedProduct && (
                    <div style={{ position: 'relative' }}>
                      <div style={{ position: 'relative' }}>
                        <Search size={16} color="rgba(255,255,255,0.5)" style={{ 
                          position: 'absolute', 
                          left: 12, 
                          top: '50%', 
                          transform: 'translateY(-50%)' 
                        }} />
                        <input
                          value={productSearchQuery}
                          onChange={(e) => setProductSearchQuery(e.target.value)}
                          placeholder="Rechercher par nom ou code..."
                          style={{
                            width: '100%',
                            padding: '12px 12px 12px 40px',
                            background: 'rgba(255,255,255,0.1)',
                            border: '1px solid rgba(255,255,255,0.2)',
                            borderRadius: 12,
                            color: '#fff',
                            fontSize: 14,
                            outline: 'none',
                          }}
                        />
                      </div>
                      
                      {/* Dropdown produits */}
                      {productSearchQuery.length > 0 && (
                        <div style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          background: '#1a3352',
                          border: '1px solid rgba(255,255,255,0.2)',
                          borderRadius: 12,
                          marginTop: 4,
                          maxHeight: 200,
                          overflowY: 'auto',
                          zIndex: 100,
                        }}>
                          {DEMO_PRODUCTS
                            .filter(p => 
                              p.name.toLowerCase().includes(productSearchQuery.toLowerCase()) ||
                              p.code.toLowerCase().includes(productSearchQuery.toLowerCase())
                            )
                            .map((product) => (
                              <button
                                key={product.id}
                                onClick={() => {
                                  setSelectedProduct(product);
                                  setProductSearchQuery('');
                                  // Un seul élément lié à la fois
                                  setSelectedService(null);
                                  setSelectedAnnonce(null);
                                }}
                                style={{
                                  width: '100%',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 10,
                                  padding: '10px 12px',
                                  background: 'none',
                                  border: 'none',
                                  borderBottom: '1px solid rgba(255,255,255,0.1)',
                                  cursor: 'pointer',
                                  textAlign: 'left',
                                }}
                              >
                                <img 
                                  src={product.image} 
                                  alt=""
                                  style={{ width: 40, height: 40, borderRadius: 6, objectFit: 'cover' }}
                                />
                                <div style={{ flex: 1 }}>
                                  <div style={{ color: '#fff', fontSize: 13, fontWeight: 500 }}>{product.name}</div>
                                  <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 11 }}>
                                    {product.code} • {product.price.toFixed(2)} €
                                  </div>
                                </div>
                              </button>
                            ))}
                          {DEMO_PRODUCTS.filter(p => 
                            p.name.toLowerCase().includes(productSearchQuery.toLowerCase()) ||
                            p.code.toLowerCase().includes(productSearchQuery.toLowerCase())
                          ).length === 0 && (
                            <div style={{ padding: 16, color: 'rgba(255,255,255,0.5)', textAlign: 'center', fontSize: 13 }}>
                              Aucun produit trouvé
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              
              {/* ══════════════════════════════════════════════════════════════
                  LIAISON COCKPIT - PRO : Prestation de Service
                  ══════════════════════════════════════════════════════════════ */}
              {userType === 'pro' && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 8, 
                    marginBottom: 8,
                  }}>
                    <Briefcase size={16} color="#F97316" />
                    <span style={{ color: '#fff', fontSize: 14, fontWeight: 500 }}>Lier une Prestation</span>
                    <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, marginLeft: 'auto' }}>de service</span>
                  </div>
                  
                  {/* Prestation sélectionnée */}
                  {selectedService && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      background: 'rgba(59,130,246,0.15)',
                      border: '1px solid rgba(59,130,246,0.4)',
                      borderRadius: 12,
                      padding: 12,
                      marginBottom: 10,
                    }}>
                      <div style={{
                        width: 48,
                        height: 48,
                        borderRadius: 8,
                        background: 'rgba(59,130,246,0.3)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                        <Briefcase size={24} color="#3B82F6" />
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ color: '#fff', fontSize: 14, fontWeight: 500 }}>{selectedService.name}</div>
                        <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12 }}>
                          {selectedService.code} • {selectedService.price.toFixed(2)} € • {selectedService.duration}
                        </div>
                      </div>
                      <button
                        onClick={() => setSelectedService(null)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
                      >
                        <X size={18} color="#fff" />
                      </button>
                    </div>
                  )}
                  
                  {/* Recherche prestation */}
                  {!selectedService && (
                    <div style={{ position: 'relative' }}>
                      <div style={{ position: 'relative' }}>
                        <Search size={16} color="rgba(255,255,255,0.5)" style={{ 
                          position: 'absolute', 
                          left: 12, 
                          top: '50%', 
                          transform: 'translateY(-50%)' 
                        }} />
                        <input
                          value={serviceSearchQuery}
                          onChange={(e) => setServiceSearchQuery(e.target.value)}
                          placeholder="Rechercher par nom ou code..."
                          style={{
                            width: '100%',
                            padding: '12px 12px 12px 40px',
                            background: 'rgba(255,255,255,0.1)',
                            border: '1px solid rgba(255,255,255,0.2)',
                            borderRadius: 12,
                            color: '#fff',
                            fontSize: 14,
                            outline: 'none',
                          }}
                        />
                      </div>
                      
                      {/* Dropdown prestations */}
                      {serviceSearchQuery.length > 0 && (
                        <div style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          background: '#1a3352',
                          border: '1px solid rgba(255,255,255,0.2)',
                          borderRadius: 12,
                          marginTop: 4,
                          maxHeight: 200,
                          overflowY: 'auto',
                          zIndex: 100,
                        }}>
                          {DEMO_SERVICES
                            .filter(s => 
                              s.name.toLowerCase().includes(serviceSearchQuery.toLowerCase()) ||
                              s.code.toLowerCase().includes(serviceSearchQuery.toLowerCase())
                            )
                            .map((service) => (
                              <button
                                key={service.id}
                                onClick={() => {
                                  setSelectedService(service);
                                  setServiceSearchQuery('');
                                  // Un seul élément lié à la fois
                                  setSelectedProduct(null);
                                  setSelectedAnnonce(null);
                                }}
                                style={{
                                  width: '100%',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 10,
                                  padding: '10px 12px',
                                  background: 'none',
                                  border: 'none',
                                  borderBottom: '1px solid rgba(255,255,255,0.1)',
                                  cursor: 'pointer',
                                  textAlign: 'left',
                                }}
                              >
                                <div style={{
                                  width: 40,
                                  height: 40,
                                  borderRadius: 6,
                                  background: 'rgba(59,130,246,0.2)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}>
                                  <Briefcase size={20} color="#3B82F6" />
                                </div>
                                <div style={{ flex: 1 }}>
                                  <div style={{ color: '#fff', fontSize: 13, fontWeight: 500 }}>{service.name}</div>
                                  <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 11 }}>
                                    {service.code} • {service.price.toFixed(2)} € • {service.duration}
                                  </div>
                                </div>
                              </button>
                            ))}
                          {DEMO_SERVICES.filter(s => 
                            s.name.toLowerCase().includes(serviceSearchQuery.toLowerCase()) ||
                            s.code.toLowerCase().includes(serviceSearchQuery.toLowerCase())
                          ).length === 0 && (
                            <div style={{ padding: 16, color: 'rgba(255,255,255,0.5)', textAlign: 'center', fontSize: 13 }}>
                              Aucune prestation trouvée
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              
              {/* ══════════════════════════════════════════════════════════════
                  LIAISON COCKPIT - PARTICULIER : Annonce
                  ══════════════════════════════════════════════════════════════ */}
              {userType === 'particulier' && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 8, 
                    marginBottom: 8,
                  }}>
                    <Megaphone size={16} color="#F97316" />
                    <span style={{ color: '#fff', fontSize: 14, fontWeight: 500 }}>Lier une Annonce</span>
                    <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, marginLeft: 'auto' }}>de mon cockpit</span>
                  </div>
                  
                  {/* Annonce sélectionnée */}
                  {selectedAnnonce && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      background: 'rgba(236,72,153,0.15)',
                      border: '1px solid rgba(236,72,153,0.4)',
                      borderRadius: 12,
                      padding: 12,
                      marginBottom: 10,
                    }}>
                      <div style={{
                        width: 48,
                        height: 48,
                        borderRadius: 8,
                        background: 'rgba(236,72,153,0.3)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                        <Megaphone size={24} color="#EC4899" />
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ color: '#fff', fontSize: 14, fontWeight: 500 }}>{selectedAnnonce.title}</div>
                        <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12 }}>
                          {selectedAnnonce.budget} • {selectedAnnonce.category}
                        </div>
                      </div>
                      <button
                        onClick={() => setSelectedAnnonce(null)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
                      >
                        <X size={18} color="#fff" />
                      </button>
                    </div>
                  )}
                  
                  {/* Recherche annonce */}
                  {!selectedAnnonce && (
                    <div style={{ position: 'relative' }}>
                      <div style={{ position: 'relative' }}>
                        <Search size={16} color="rgba(255,255,255,0.5)" style={{ 
                          position: 'absolute', 
                          left: 12, 
                          top: '50%', 
                          transform: 'translateY(-50%)' 
                        }} />
                        <input
                          value={annonceSearchQuery}
                          onChange={(e) => setAnnonceSearchQuery(e.target.value)}
                          placeholder="Rechercher une annonce..."
                          style={{
                            width: '100%',
                            padding: '12px 12px 12px 40px',
                            background: 'rgba(255,255,255,0.1)',
                            border: '1px solid rgba(255,255,255,0.2)',
                            borderRadius: 12,
                            color: '#fff',
                            fontSize: 14,
                            outline: 'none',
                          }}
                        />
                      </div>
                      
                      {/* Dropdown annonces - affiche toutes les annonces actives */}
                      <div style={{
                        marginTop: 8,
                        background: 'rgba(255,255,255,0.05)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: 12,
                        maxHeight: 180,
                        overflowY: 'auto',
                      }}>
                        {DEMO_ANNONCES
                          .filter(a => 
                            a.status === 'active' &&
                            (annonceSearchQuery === '' || 
                             a.title.toLowerCase().includes(annonceSearchQuery.toLowerCase()) ||
                             a.category.toLowerCase().includes(annonceSearchQuery.toLowerCase()))
                          )
                          .map((annonce) => (
                            <button
                              key={annonce.id}
                              onClick={() => {
                                setSelectedAnnonce(annonce);
                                setAnnonceSearchQuery('');
                                setSelectedProduct(null);
                                setSelectedService(null);
                              }}
                              style={{
                                width: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 10,
                                padding: '10px 12px',
                                background: 'none',
                                border: 'none',
                                borderBottom: '1px solid rgba(255,255,255,0.1)',
                                cursor: 'pointer',
                                textAlign: 'left',
                              }}
                            >
                              <div style={{
                                width: 36,
                                height: 36,
                                borderRadius: 6,
                                background: 'rgba(236,72,153,0.2)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}>
                                <Megaphone size={18} color="#EC4899" />
                              </div>
                              <div style={{ flex: 1 }}>
                                <div style={{ color: '#fff', fontSize: 13, fontWeight: 500 }}>{annonce.title}</div>
                                <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 11 }}>
                                  {annonce.budget} • {annonce.date}
                                </div>
                              </div>
                              <span style={{
                                fontSize: 10,
                                fontWeight: 600,
                                color: '#10B981',
                                background: 'rgba(16,185,129,0.2)',
                                padding: '2px 6px',
                                borderRadius: 4,
                              }}>
                                ACTIVE
                              </span>
                            </button>
                          ))}
                        {DEMO_ANNONCES.filter(a => 
                          a.status === 'active' &&
                          (annonceSearchQuery === '' || 
                           a.title.toLowerCase().includes(annonceSearchQuery.toLowerCase()))
                        ).length === 0 && (
                          <div style={{ padding: 16, color: 'rgba(255,255,255,0.5)', textAlign: 'center', fontSize: 13 }}>
                            Aucune annonce active
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {/* Résumé liaison Cockpit */}
              {(selectedProduct || selectedService || selectedAnnonce) && (
                <div style={{
                  background: 'rgba(249,115,22,0.1)',
                  border: '1px solid rgba(249,115,22,0.3)',
                  borderRadius: 12,
                  padding: 12,
                  marginBottom: 16,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                }}>
                  <ShoppingBag size={18} color="#F97316" />
                  <span style={{ color: '#F97316', fontSize: 13, fontWeight: 500 }}>
                    {selectedProduct && `Produit lié : ${selectedProduct.name}`}
                    {selectedService && `Prestation liée : ${selectedService.name}`}
                    {selectedAnnonce && `Annonce liée : ${selectedAnnonce.title}`}
                  </span>
                </div>
              )}
              
              {/* Bouton Publier */}
              <button
                onClick={handlePublish}
                disabled={isUploading || !selectedMetier || (captureMode === 'photo' ? photos.length === 0 : videoClips.length === 0)}
                style={{
                  width: '100%',
                  padding: 16,
                  background: (isUploading || !selectedMetier) ? 'rgba(249,115,22,0.4)' : 'linear-gradient(135deg, #F97316 0%, #EA580C 100%)',
                  border: 'none',
                  borderRadius: 12,
                  cursor: (isUploading || !selectedMetier) ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  opacity: !selectedMetier ? 0.6 : 1,
                }}
              >
                {isUploading ? (
                  <>
                    <Loader2 size={20} color="#fff" style={{ animation: 'spin 1s linear infinite' }} />
                    <span style={{ color: '#fff', fontSize: 16, fontWeight: 600 }}>Publication...</span>
                  </>
                ) : !selectedMetier ? (
                  <span style={{ color: '#fff', fontSize: 16, fontWeight: 600 }}>Sélectionne un univers</span>
                ) : (
                  <>
                    <Check size={20} color="#fff" />
                    <span style={{ color: '#fff', fontSize: 16, fontWeight: 600 }}>
                      {captureMode === 'photo' ? 'Publier' : (captureMode === 'story' ? 'Partager la Story' : 'Publier le Reel')}
                    </span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* CSS animations */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </motion.div>
  );
}

// Données de démo pour les reels
const DEMO_REELS = [
  {
    id: 'reel_1',
    videoUrl: null,
    thumbnail: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=400&h=800&fit=crop',
    author: {
      name: 'Martin Électricité',
      username: 'martin_elec',
      avatar: null,
      verified: true,
      metier: 'Électricien',
    },
    description: 'Installation tableau électrique dernière génération ⚡️ #électricité #renovation #artisan',
    music: 'Son original - Martin Électricité',
    likes: 1247,
    comments: 89,
    shares: 34,
    views: 15600,
  },
  {
    id: 'reel_2',
    videoUrl: null,
    thumbnail: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&h=800&fit=crop',
    author: {
      name: 'Plomberie Pro 13',
      username: 'plomberie_pro',
      avatar: null,
      verified: true,
      metier: 'Plombier',
    },
    description: 'Rénovation complète salle de bain 🚿 Avant/Après incroyable ! #plomberie #salledebain',
    music: 'Trending Sound - Réno',
    likes: 2341,
    comments: 156,
    shares: 78,
    views: 28900,
  },
  {
    id: 'reel_3',
    videoUrl: null,
    thumbnail: 'https://images.unsplash.com/photo-1585771724684-38269d6639fd?w=400&h=800&fit=crop',
    author: {
      name: 'Jardins & Co',
      username: 'jardins_co',
      avatar: null,
      verified: true,
      metier: 'Paysagiste',
    },
    description: 'Transformation jardin en 3 jours 🌿 Le résultat est fou ! #jardin #paysagiste #green',
    music: 'Nature Vibes - Chill',
    likes: 3456,
    comments: 234,
    shares: 123,
    views: 45000,
  },
  {
    id: 'reel_4',
    videoUrl: null,
    thumbnail: 'https://images.unsplash.com/photo-1562259949-e8e7689d7828?w=400&h=800&fit=crop',
    author: {
      name: 'Peinture Déco Plus',
      username: 'peinture_deco',
      avatar: null,
      verified: false,
      metier: 'Peintre',
    },
    description: 'Technique de peinture effet béton ciré 🎨 Tuto rapide ! #peinture #deco #astuce',
    music: 'Tutorial Beat',
    likes: 892,
    comments: 67,
    shares: 45,
    views: 12300,
  },
];

// Composant pour un seul Reel
function ReelItem({ reel, isActive, onLike, onComment, onShare, onPublish }) {
  const [isLiked, setIsLiked] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [isPlaying, setIsPlaying] = useState(isActive);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const videoRef = useRef(null);

  // Play/Pause quand actif
  useEffect(() => {
    setIsPlaying(isActive);
  }, [isActive]);

  const handleLike = () => {
    setIsLiked(!isLiked);
    onLike?.(reel.id);
  };

  const togglePlay = () => {
    setIsPlaying(!isPlaying);
  };

  const toggleMute = (e) => {
    e.stopPropagation();
    setIsMuted(!isMuted);
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  return (
    <div 
      className="reel-item"
      onClick={togglePlay}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        background: '#000',
        overflow: 'hidden',
      }}
    >
      {/* ═══ VIDÉO / IMAGE PLEIN ÉCRAN ═══ */}
      {reel.videoUrl ? (
        <video
          ref={videoRef}
          src={reel.videoUrl}
          loop
          muted={isMuted}
          playsInline
          autoPlay={isActive}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
        />
      ) : (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundImage: `url(${reel.thumbnail})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
      )}

      {/* ═══ OVERLAY GRADIENT ═══ */}
      <div style={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        height: '50%',
        background: 'linear-gradient(to top, rgba(0,0,0,0.8) 0%, transparent 100%)',
        pointerEvents: 'none',
      }} />

      {/* ═══ BOUTON PLAY CENTRAL (si pause) ═══ */}
      <AnimatePresence>
        {!isPlaying && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 80,
              height: 80,
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.2)',
              backdropFilter: 'blur(10px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Play size={40} fill="white" color="white" />
          </motion.div>
        )}
      </AnimatePresence>

      {/* ═══ ACTIONS À DROITE - EN BAS ═══ */}
      <div style={{
        position: 'absolute',
        right: 12,
        bottom: 24,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 20,
      }}>
        {/* Bouton Publier */}
        <button
          onClick={(e) => { e.stopPropagation(); onPublish?.(); }}
          style={{
            width: 44,
            height: 44,
            borderRadius: '50%',
            background: '#F97316',
            border: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
          }}
        >
          <span style={{ color: '#fff', fontSize: 24, fontWeight: 500, lineHeight: 1 }}>+</span>
        </button>

        {/* Like */}
        <button
          onClick={(e) => { e.stopPropagation(); handleLike(); }}
          style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}
        >
          <Heart 
            size={28} 
            fill={isLiked ? '#ff2d55' : 'transparent'} 
            color={isLiked ? '#ff2d55' : '#fff'}
            style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }}
          />
          <span style={{ color: '#fff', fontSize: 12, fontWeight: 600, textShadow: '0 1px 3px rgba(0,0,0,0.5)' }}>
            {formatNumber(reel.likes + (isLiked ? 1 : 0))}
          </span>
        </button>

        {/* Comment */}
        <button
          onClick={(e) => { e.stopPropagation(); onComment?.(reel.id); }}
          style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}
        >
          <MessageCircle 
            size={28} 
            color="#fff"
            style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }}
          />
          <span style={{ color: '#fff', fontSize: 12, fontWeight: 600, textShadow: '0 1px 3px rgba(0,0,0,0.5)' }}>
            {formatNumber(reel.comments)}
          </span>
        </button>

        {/* Share */}
        <button
          onClick={(e) => { e.stopPropagation(); onShare?.(reel.id); }}
          style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}
        >
          <Share2 
            size={28} 
            color="#fff"
            style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }}
          />
          <span style={{ color: '#fff', fontSize: 12, fontWeight: 600, textShadow: '0 1px 3px rgba(0,0,0,0.5)' }}>
            {formatNumber(reel.shares)}
          </span>
        </button>

        {/* Bookmark */}
        <button
          onClick={(e) => e.stopPropagation()}
          style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: 44 }}
        >
          <Bookmark 
            size={28} 
            color="#fff"
            style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }}
          />
        </button>

        {/* More */}
        <button
          onClick={(e) => e.stopPropagation()}
          style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: 44 }}
        >
          <MoreHorizontal 
            size={28} 
            color="#fff"
            style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }}
          />
        </button>
      </div>

      {/* ═══ INFO AUTEUR EN BAS ═══ */}
      <div style={{
        position: 'absolute',
        left: 12,
        right: 70,
        bottom: 16,
      }}>
        {/* Nom auteur */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
          <span style={{ 
            color: '#fff', 
            fontSize: 14, 
            fontWeight: 700,
            textShadow: '0 1px 3px rgba(0,0,0,0.5)',
          }}>
            @{reel.author.username}
          </span>
          {reel.author.verified && (
            <span style={{
              background: '#3b82f6',
              color: '#fff',
              fontSize: 10,
              fontWeight: 700,
              padding: '2px 6px',
              borderRadius: 4,
            }}>
              ✓
            </span>
          )}
          <span style={{
            background: 'rgba(255,255,255,0.2)',
            backdropFilter: 'blur(4px)',
            color: '#fff',
            fontSize: 10,
            fontWeight: 600,
            padding: '2px 8px',
            borderRadius: 20,
          }}>
            {reel.author.metier}
          </span>
        </div>

        {/* Description - max 125 caractères comme Instagram */}
        <div style={{ position: 'relative' }}>
          <p 
            onClick={(e) => { e.stopPropagation(); setShowFullDescription(!showFullDescription); }}
            style={{ 
              color: '#fff', 
              fontSize: 13, 
              lineHeight: 1.4,
              textShadow: '0 1px 3px rgba(0,0,0,0.5)',
              cursor: 'pointer',
              margin: 0,
            }}
          >
            {showFullDescription ? reel.description : (
              reel.description.length > 80 ? (
                <>
                  {reel.description.slice(0, 80)}...
                  <span style={{ 
                    color: 'rgba(255,255,255,0.7)', 
                    fontWeight: 500,
                    marginLeft: 4,
                  }}>
                    plus
                  </span>
                </>
              ) : reel.description
            )}
          </p>
        </div>

        {/* Musique */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 6, 
          marginTop: 8,
        }}>
          <Music size={12} color="#fff" />
          <div style={{
            overflow: 'hidden',
            flex: 1,
          }}>
            <motion.span
              animate={{ x: isPlaying ? [0, -100, 0] : 0 }}
              transition={{ duration: 8, repeat: Infinity, ease: 'linear' }}
              style={{ 
                color: '#fff', 
                fontSize: 11,
                whiteSpace: 'nowrap',
                display: 'inline-block',
                opacity: 0.9,
              }}
            >
              {reel.music}
            </motion.span>
          </div>
        </div>
      </div>

      {/* ═══ BOUTON MUTE ═══ */}
      <button
        onClick={toggleMute}
        style={{
          position: 'absolute',
          right: 12,
          top: 60,
          width: 36,
          height: 36,
          borderRadius: '50%',
          background: 'rgba(0,0,0,0.4)',
          backdropFilter: 'blur(4px)',
          border: 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
        }}
      >
        {isMuted ? (
          <VolumeX size={18} color="#fff" />
        ) : (
          <Volume2 size={18} color="#fff" />
        )}
      </button>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// PAGE PRINCIPALE REELS
// ══════════════════════════════════════════════════════════════════════════════

export default function ReelsPage() {
  const navigate = useNavigate();
  const outletContext = useOutletContext();
  const isInReseauLayout = !!outletContext;
  
  const [reels, setReels] = useState(DEMO_REELS);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [activeTab, setActiveTab] = useState('tous');
  const [activeMetier, setActiveMetier] = useState('jardinier');
  const [showPublishModal, setShowPublishModal] = useState(false);
  const containerRef = useRef(null);

  // Charger les reels depuis l'API
  useEffect(() => {
    const fetchReels = async () => {
      try {
        const response = await fetch(`${API_URL}/api/reseau/reels?limit=20`);
        if (response.ok) {
          const data = await response.json();
          if (data.reels && data.reels.length > 0) {
            // Transformer les données API en format attendu
            const formattedReels = data.reels.map(r => ({
              id: r.id,
              videoUrl: r.video_url,
              thumbnail: r.thumbnail || 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=400&h=800&fit=crop',
              author: {
                name: r.author?.display_name || r.author?.name || 'Artisan',
                username: r.author?.id?.replace('pro_', '') || 'artisan',
                avatar: r.author?.profile_photo,
                verified: r.author?.is_verified || false,
                metier: r.author?.metier || r.metier_label || 'Artisan',
              },
              description: r.content || r.title || '',
              music: 'Son original',
              likes: r.likes || 0,
              comments: r.comments || 0,
              shares: r.shares || 0,
              views: r.views || 0,
            }));
            setReels([...formattedReels, ...DEMO_REELS]);
          }
        }
      } catch (error) {
        console.error('Error fetching reels:', error);
      }
    };
    fetchReels();
  }, []);

  // Gestion du scroll snap
  const handleScroll = useCallback(() => {
    if (containerRef.current) {
      const scrollTop = containerRef.current.scrollTop;
      const itemHeight = containerRef.current.clientHeight;
      const newIndex = Math.round(scrollTop / itemHeight);
      if (newIndex !== currentIndex && newIndex >= 0 && newIndex < reels.length) {
        setCurrentIndex(newIndex);
      }
    }
  }, [currentIndex, reels.length]);

  // Scroll to specific reel
  const scrollToReel = (index) => {
    if (containerRef.current) {
      containerRef.current.scrollTo({
        top: index * containerRef.current.clientHeight,
        behavior: 'smooth',
      });
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      width: '100vw',
      height: '100dvh', // iOS Safari: dynamic viewport height
      minHeight: '-webkit-fill-available', // iOS Safari fallback
      background: '#000',
      overflow: 'hidden',
      zIndex: 9999,
      WebkitOverflowScrolling: 'touch',
    }}>
      {/* ═══ BOUTON RETOUR DISCRET EN HAUT À GAUCHE ═══ */}
      <button
        onClick={() => navigate('/reseau')}
        style={{
          position: 'absolute',
          top: 16,
          left: 16,
          zIndex: 100,
          width: 40,
          height: 40,
          borderRadius: '50%',
          background: 'rgba(0,0,0,0.4)',
          backdropFilter: 'blur(8px)',
          border: 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
        }}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="19" y1="12" x2="5" y2="12"/>
          <polyline points="12 19 5 12 12 5"/>
        </svg>
      </button>

      {/* ═══ ONGLETS FLOTTANTS STYLE INSTAGRAM ═══ */}
      <div style={{
        position: 'absolute',
        top: 14,
        left: 72,
        right: 12,
        zIndex: 100,
        display: 'flex',
        gap: 10,
        overflowX: 'auto',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        paddingRight: 20,
      }}>
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              background: 'transparent',
              border: 'none',
              padding: '4px 2px',
              cursor: 'pointer',
              position: 'relative',
              flexShrink: 0,
            }}
          >
            <span style={{
              color: '#fff',
              fontSize: 16,
              fontWeight: activeTab === tab.id ? 700 : 500,
              textShadow: '0 1px 6px rgba(0,0,0,0.8)',
              opacity: activeTab === tab.id ? 1 : 0.75,
              transition: 'all 0.2s ease',
              whiteSpace: 'nowrap',
            }}>
              {tab.label}
            </span>
            {activeTab === tab.id && (
              <div style={{
                position: 'absolute',
                bottom: 0,
                left: '50%',
                transform: 'translateX(-50%)',
                width: 18,
                height: 2,
                background: '#fff',
                borderRadius: 1,
              }} />
            )}
          </button>
        ))}
      </div>

      {/* ═══ SOUS-FILTRES UNIVERS (2ème ligne) ═══ */}
      {activeTab === 'univers' && (
        <div style={{
          position: 'absolute',
          top: 48,
          left: 16,
          right: 16,
          zIndex: 100,
          display: 'flex',
          gap: 14,
          overflowX: 'auto',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          paddingBottom: 4,
        }}>
          {METIER_FILTERS.map((metier) => (
            <button
              key={metier.id}
              onClick={() => setActiveMetier(metier.id)}
              style={{
                background: 'transparent',
                border: 'none',
                padding: '4px 0',
                cursor: 'pointer',
                position: 'relative',
                flexShrink: 0,
              }}
            >
              <span style={{
                color: '#fff',
                fontSize: 14,
                fontWeight: activeMetier === metier.id ? 700 : 500,
                textShadow: '0 1px 6px rgba(0,0,0,0.8)',
                opacity: activeMetier === metier.id ? 1 : 0.7,
                transition: 'all 0.2s ease',
                whiteSpace: 'nowrap',
              }}>
                {metier.emoji} {metier.label}
              </span>
              {activeMetier === metier.id && (
                <div style={{
                  position: 'absolute',
                  bottom: 0,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: 16,
                  height: 2,
                  background: '#fff',
                  borderRadius: 1,
                }} />
              )}
            </button>
          ))}
        </div>
      )}

      {/* ═══ CONTENEUR REELS - PLEIN ÉCRAN TOTAL ═══ */}
      <div
        ref={containerRef}
        onScroll={handleScroll}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: '100%',
          height: '100dvh', // iOS Safari fix
          minHeight: '-webkit-fill-available',
          overflowY: 'scroll',
          overflowX: 'hidden',
          scrollSnapType: 'y mandatory',
          WebkitOverflowScrolling: 'touch', // iOS smooth scroll
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
        }}
      >
        {reels.map((reel, index) => (
          <div
            key={reel.id}
            style={{
              position: 'relative',
              width: '100%',
              height: '100dvh', // iOS Safari fix - each reel takes full viewport
              minHeight: '-webkit-fill-available',
              scrollSnapAlign: 'start',
              scrollSnapStop: 'always',
            }}
          >
            <ReelItem
              reel={reel}
              isActive={index === currentIndex}
              onLike={(id) => console.log('Like:', id)}
              onComment={(id) => console.log('Comment:', id)}
              onShare={(id) => console.log('Share:', id)}
              onPublish={() => setShowPublishModal(true)}
            />
          </div>
        ))}
      </div>

      {/* ═══ INDICATEURS DE PROGRESSION ═══ */}
      <div style={{
        position: 'absolute',
        right: 4,
        top: '50%',
        transform: 'translateY(-50%)',
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        zIndex: 50,
      }}>
        {reels.slice(0, 5).map((_, index) => (
          <div
            key={index}
            onClick={() => scrollToReel(index)}
            style={{
              width: 3,
              height: index === currentIndex ? 20 : 12,
              borderRadius: 2,
              background: index === currentIndex ? '#fff' : 'rgba(255,255,255,0.4)',
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
          />
        ))}
      </div>

      {/* CSS pour cacher la scrollbar et fix iOS */}
      <style>{`
        div::-webkit-scrollbar {
          display: none;
        }
        /* iOS Safari viewport fix */
        @supports (-webkit-touch-callout: none) {
          .reel-item {
            min-height: -webkit-fill-available;
          }
        }
      `}</style>
      
      {/* Modal Publier Reel */}
      <AnimatePresence>
        {showPublishModal && (
          <PublishReelModal
            isOpen={showPublishModal}
            onClose={() => setShowPublishModal(false)}
            onPublish={() => {
              // Rafraîchir les reels après publication
              console.log('Reel publié!');
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
